/* global Word, Office */

import Logger from "@/utility/logger";
import type { SeqType } from "./wordInsertion";

type CaptionType = "Table" | "Figure";
type CaptionPlacement = Word.InsertLocation.before | Word.InsertLocation.after;

let figureCounter = 0;
let tableCounter = 0;

export const resetCaptionCounters = () => {
  figureCounter = 0;
  tableCounter = 0;
};

export const getNextCaptionNumber = (type: CaptionType): number => {
  if (type === "Figure") {
    figureCounter += 1;
    return figureCounter;
  }
  tableCounter += 1;
  return tableCounter;
};

export interface TableInfo {
  tableRange: Word.Range;
  bookmarkId: string;
  captionDescription: string;
}

export interface FigureInfo {
  imageParagraphRange: Word.Range;
  bookmarkId: string;
  captionDescription: string;
}

const SUPERSCRIPT_MARKERS =
  "\u2070\u00B9\u00B2\u00B3\u2074\u2075\u2076\u2077\u2078\u2079\u207A\u207B\u207C\u207D\u207E";
const SUBSCRIPT_MARKERS = "\u2080\u2081\u2082\u2083\u2084\u2085\u2086\u2087\u2088\u2089\u208A\u208B\u208C\u208D\u208E";

const getScriptMarkerType = (char: string): "superscript" | "subscript" | null => {
  if (SUPERSCRIPT_MARKERS.includes(char)) {
    return "superscript";
  }

  if (SUBSCRIPT_MARKERS.includes(char)) {
    return "subscript";
  }

  return null;
};

export const applyScriptMarkerFormattingToRange = async (context: Word.RequestContext, targetRange: Word.Range) => {
  targetRange.load("text");
  await context.sync();

  const text = targetRange.text || "";

  // 1. Process HTML <sup> and <sub> tags
  const htmlScriptRegex = /<(sup|sub)\b[^>]*>(.*?)<\/\1>/gi;
  let htmlMatch: RegExpExecArray | null;
  const htmlReplacements: Array<{ fullMatch: string; innerText: string; type: "superscript" | "subscript" }> = [];

  while ((htmlMatch = htmlScriptRegex.exec(text)) !== null) {
    if (htmlMatch[2]) {
      htmlReplacements.push({
        fullMatch: htmlMatch[0],
        innerText: htmlMatch[2],
        type: htmlMatch[1].toLowerCase() === "sup" ? "superscript" : "subscript",
      });
    }
  }

  for (const { fullMatch, innerText, type } of htmlReplacements) {
    const searchResults = targetRange.search(fullMatch, {
      matchCase: false,
      matchWholeWord: false,
      ignorePunct: false,
      ignoreSpace: false,
    });
    searchResults.load("items");
    await context.sync();

    for (const matchRange of searchResults.items) {
      matchRange.insertText(innerText, Word.InsertLocation.replace);
      matchRange.font.superscript = type === "superscript";
      matchRange.font.subscript = type === "subscript";
    }
    await context.sync();
  }

  // 2. Process Unicode script glyphs (¹ ² …)
  const markersToFormat = Array.from(new Set(Array.from(targetRange.text || "")))
    .map((char) => ({ char, type: getScriptMarkerType(char) }))
    .filter((entry): entry is { char: string; type: "superscript" | "subscript" } => !!entry.type);

  if (markersToFormat.length === 0) {
    return;
  }

  const searchResults = markersToFormat.map(({ char, type }) => {
    const results = targetRange.search(char, {
      matchCase: true,
      matchWholeWord: false,
      ignorePunct: false,
      ignoreSpace: false,
    });

    results.load("items");

    return {
      type,
      results,
    };
  });

  await context.sync();

  for (const { type, results } of searchResults) {
    for (const markerRange of results.items) {
      markerRange.font.superscript = type === "superscript";
      markerRange.font.subscript = type === "subscript";
    }
  }

  await context.sync();
};

// Get paragraph adjacent to a range for caption placement
export const getCaptionParagraph = async (
  context: Word.RequestContext,
  range: Word.Range,
  position: CaptionPlacement = Word.InsertLocation.before,
  captionType: CaptionType = "Table"
): Promise<Word.Paragraph | null> => {
  return await Word.run(async () => {
    range.load("paragraphs");
    await context.sync();

    // For Figures, caption goes AFTER the image
    // For Tables, caption goes BEFORE the table
    const shouldUseBefore =
      captionType === "Table" ? position === Word.InsertLocation.before : position === Word.InsertLocation.after;

    const boundaryParagraph = shouldUseBefore ? range.paragraphs.getFirst() : range.paragraphs.getLast();

    const targetParagraph = shouldUseBefore ? boundaryParagraph.getPrevious() : boundaryParagraph.getNext();

    targetParagraph.load();
    await context.sync();

    return targetParagraph;
  });
};

// Insert SEQ field for automatic numbering
export const insertSeqField = async (
  context: Word.RequestContext,
  seqType: CaptionType = "Table",
  range: Word.Range
) => {
  try {
    range.insertField(Word.InsertLocation.start, Word.FieldType.seq, `${seqType} \\* ARABIC`, true);
    await context.sync();
    await updateAllFields(context, seqType);
  } catch (fieldError) {
    Logger.warn(`wordCaptions.ts: insertSeqField failed, falling back to static numbering:`, fieldError);
    try {
      const fallbackNumber = getNextCaptionNumber(seqType);
      range.insertText(`${fallbackNumber}`, Word.InsertLocation.start);
      await context.sync();
    } catch (fallbackError) {
      Logger.warn(`wordCaptions.ts: insertSeqField static fallback failed:`, fallbackError);
    }
  }
};

// Update all SEQ fields of a specific type
export const updateAllFields = async (context: Word.RequestContext, seqType: CaptionType = "Table") => {
  try {
    const fields = context.document.body.fields;
    fields.load(["items", "code", "type", "locked"]);
    await context.sync();

    const targetFields = fields.items.filter(
      (field) => field.type === Word.FieldType.seq && !field.locked && field.code.includes(seqType)
    );

    targetFields.forEach((field) => field.updateResult());
    await context.sync();
  } catch (error) {
    Logger.warn(`wordCaptions.ts: updateAllFields failed:`, error);
  }
};

export const updateDocumentFields = async (context: Word.RequestContext) => {
  await Word.run(async () => {
    try {
      if (!context.document.body || !context.document.body.fields) return;
      const fields = context.document.body.fields;
      fields.load(["items", "type", "locked"]);
      await context.sync();

      fields.items.forEach((field) => {
        if (!field.locked) {
          try {
            field.updateResult();
          } catch (_) {}
        }
      });
      await context.sync();
    } catch (err) {
      Logger.warn("wordCaptions.ts: Failed to update document fields:", err);
    }
  });
};

// Generate unique bookmark ID
export const generateUniqueBookmarkId = async (captionType: CaptionType): Promise<string> => {
  const timestamp = Date.now().toString(36);
  const randomPart = Math.random().toString(36).substring(2, 6);
  const bookmarkId = `${captionType}_${timestamp}_${randomPart}`;

  // Simplified - just check length and return
  if (bookmarkId.length <= 40) {
    return bookmarkId;
  }

  return `${captionType}_${Date.now()}`;
};

// Extract table label (numeric or Roman/alphanumeric) from caption text
export const extractTableLabel = (captionText: string, captionType: CaptionType = "Table"): string => {
  const regex = new RegExp(`${captionType}\\s+([A-Za-z0-9.#-]+)`, "i");
  const match = captionText.match(regex);
  return match ? `${captionType} ${match[1]}` : `${captionType} 1`;
};

const extractTableNumber = (captionText: string): number => {
  const match = captionText.match(/Table\s+(\d+)/i);
  return match ? parseInt(match[1]) : 1;
};

// Generate search pattern for cross-references
const generateSearchPattern = (labelOrNumber: string | number, captionType: CaptionType = "Table"): string => {
  if (typeof labelOrNumber === "string" && labelOrNumber.length > 0) {
    return labelOrNumber.trim().toLowerCase().startsWith(`${captionType.toLowerCase()} `)
      ? labelOrNumber.trim()
      : `${captionType} ${labelOrNumber.trim()}`;
  }
  return `${captionType} ${labelOrNumber}`;
};

// Find cross-references in text
const findCrossReferences = async (
  context: Word.RequestContext,
  searchText: string,
  sectionRange: Word.Range,
  excludeRanges: Word.Range[] = []
): Promise<Word.Range[]> => {
  return await Word.run(async () => {
    const searchResults = sectionRange.search(searchText, {
      ignorePunct: true,
      ignoreSpace: false,
      matchCase: false,
      matchWholeWord: true,
      matchWildcards: false,
    });

    searchResults.load(["items"]);
    await context.sync();

    if (searchResults.items.length === 0) {
      Logger.debug(`wordCaptions.ts: No occurrences of "${searchText}" found`);
      return [];
    }

    // Load text for all results
    searchResults.items.forEach((result) => result.load(["text"]));
    await context.sync();

    // Filter out excluded ranges
    const validResults = [];
    for (const result of searchResults.items) {
      let isExcluded = false;

      for (const excludeRange of excludeRanges) {
        try {
          result.load(["start", "end"]);
          excludeRange.load(["start", "end"]);
          await context.sync();

          const intersection = result.intersectWith(excludeRange);
          intersection.load("isNullObject");
          await context.sync();

          if (!intersection.isNullObject) {
            Logger.debug(`wordCaptions.ts: Excluded "${result.text}" (overlaps with table/caption)`);
            isExcluded = true;
            break;
          }
        } catch (error) {
          // No overlap - continue
          Logger.debug("wordCaptions.ts: The error is cool", error);
        }
      }

      if (!isExcluded) {
        validResults.push(result);
        Logger.debug(`wordCaptions.ts: Found valid cross-reference: "${result.text}"`);
      }
    }

    return validResults;
  });
};

// Insert cross-reference field
const insertCrossReference = async (context: Word.RequestContext, bookmarkName: string, range: Word.Range) => {
  await Word.run(async () => {
    // Store original formatting
    range.load(["font/name", "font/size", "font/bold", "font/italic", "font/color", "font/highlightColor"]);
    await context.sync();

    const originalFont = {
      name: range.font.name,
      size: range.font.size,
      bold: range.font.bold,
      italic: range.font.italic,
      color: range.font.color,
      highlightColor: range.font.highlightColor,
    };

    // Insert REF field with "Table N" format
    const fieldCode = `${bookmarkName} \\# "Table 0" \\h`;
    range.insertField(Word.InsertLocation.replace, Word.FieldType.ref, fieldCode, true);
    await context.sync();

    // Restore and enhance formatting
    range.load([
      "font/name",
      "font/size",
      "font/bold",
      "font/italic",
      "font/color",
      "font/highlightColor",
      "font/underline",
    ]);
    await context.sync();

    range.font.name = originalFont.name;
    range.font.size = originalFont.size;
    range.font.bold = true; // Make cross-references bold
    range.font.italic = originalFont.italic;
    range.font.color = originalFont.color;
    range.font.highlightColor = originalFont.highlightColor;
    range.font.underline = Word.UnderlineType.single;

    await context.sync();
    Logger.debug(`wordCaptions.ts: Cross-reference inserted for ${bookmarkName}`);
  });
};

// Add cross-references for a search pattern
const addCrossReference = async (
  context: Word.RequestContext,
  searchText: string,
  bookmarkId: string,
  sectionRange: Word.Range,
  excludeRanges: Word.Range[] = []
) => {
  await Word.run(async () => {
    const references = await findCrossReferences(context, searchText, sectionRange, excludeRanges);

    if (references.length === 0) {
      Logger.debug(`wordCaptions.ts: No cross-references found for "${searchText}"`);
      return;
    }

    Logger.debug(`wordCaptions.ts: Creating ${references.length} cross-references for "${searchText}"`);

    // Insert cross-references for all found matches
    for (const ref of references) {
      await insertCrossReference(context, bookmarkId, ref);
    }

    await context.sync();
  });
};

// Main function: Create captions and cross-references for multiple tables
export const createCaptionsForSection = async (
  context: Word.RequestContext,
  sectionRange: Word.Range,
  tableInfos: TableInfo[],
  captionType: CaptionType = "Table"
) => {
  await Word.run(async () => {
    const isWordDesktop =
      Office.context.platform === Office.PlatformType.Mac || Office.context.platform === Office.PlatformType.PC;

    if (!isWordDesktop) {
      Logger.warn("wordCaptions.ts: Captions only supported in Word Desktop");
      return;
    }

    Logger.debug(`wordCaptions.ts: Processing ${tableInfos.length} tables for captions and cross-references`);

    // Step 1: Create all captions and collect table numbers
    const tableNumbers: number[] = [];

    for (let i = 0; i < tableInfos.length; i++) {
      const tableInfo = tableInfos[i];
      const targetParagraph = await getCaptionParagraph(
        context,
        tableInfo.tableRange,
        Word.InsertLocation.before,
        captionType
      );

      targetParagraph.load("isNullObject");
      await context.sync();

      if (!targetParagraph.isNullObject) {
        // Load the text of the paragraph above the table
        targetParagraph.load("text");
        await context.sync();

        // Use the paragraph text as the caption description
        const captionDescription = targetParagraph.text.trim();

        // Replace the paragraph with the caption
        targetParagraph.insertText(`${captionType} `, "Replace");
        await context.sync();

        await insertSeqField(context, captionType, targetParagraph.getRange("End"));
        await context.sync();

        targetParagraph.insertText(`. ${captionDescription}`, "End");
        await context.sync();

        // Set entire paragraph to bold
        targetParagraph.font.bold = true;
        await context.sync();

        // Add bookmark to caption
        targetParagraph.getRange().insertBookmark(tableInfo.bookmarkId);
        await context.sync();

        // Get the actual table label from caption
        await updateAllFields(context, captionType);
        targetParagraph.load("text");
        await context.sync();

        const tableLabel = extractTableLabel(targetParagraph.text.trim(), captionType);
        tableNumbers.push(tableLabel as any);

        Logger.debug(`wordCaptions.ts: Created caption: "${tableLabel}. ${tableInfo.captionDescription}"`);
      }
    }

    // Step 2: Collect all ranges to exclude from cross-reference search.
    // Exclude only the caption itself so table header cells and content
    // remain eligible for cross-linking and cross-referencing.
    const excludeRanges: Word.Range[] = [];

    for (const tableInfo of tableInfos) {
      // Exclude caption
      const captionParagraph = await getCaptionParagraph(
        context,
        tableInfo.tableRange,
        Word.InsertLocation.before,
        captionType
      );
      captionParagraph.load("isNullObject");
      await context.sync();

      if (!captionParagraph.isNullObject) {
        excludeRanges.push(captionParagraph.getRange());
      }
    }

    // Step 3: Create cross-references for each table
    for (let i = 0; i < tableInfos.length; i++) {
      const tableInfo = tableInfos[i];
      const tableNumber = tableNumbers[i];
      const searchPattern = generateSearchPattern(tableNumber);

      Logger.debug(`wordCaptions.ts: Searching for "${searchPattern}" to create cross-references`);

      try {
        await addCrossReference(context, searchPattern, tableInfo.bookmarkId, sectionRange, excludeRanges);
      } catch (error) {
        Logger.error(`wordCaptions.ts: Error creating cross-references for "${searchPattern}":`, error);
      }
    }

    // Step 4: Final field update
    await updateAllFields(context, captionType);
    Logger.debug("wordCaptions.ts: Caption and cross-reference processing complete");
  });
};

// Single table caption function (for manual use)
export const createCaption = async (
  context: Word.RequestContext,
  captionType: CaptionType = "Table",
  tableRange: Word.Range,
  placement: CaptionPlacement = Word.InsertLocation.before,
  sectionRange: Word.Range | null = null
) => {
  await Word.run(async () => {
    const isWordDesktop =
      Office.context.platform === Office.PlatformType.Mac || Office.context.platform === Office.PlatformType.PC;

    if (!isWordDesktop) {
      Logger.warn("wordCaptions.ts: Captions only supported in Word Desktop");
      return;
    }

    const targetParagraph = await getCaptionParagraph(context, tableRange, placement, captionType);
    targetParagraph.load("isNullObject");
    await context.sync();

    let existingDescription = "";

    if (!targetParagraph.isNullObject) {
      targetParagraph.load("text");
      await context.sync();

      const paragraphText = targetParagraph.text.trim();
      const captionPattern = new RegExp(`^${captionType}\\s+(\\d+)\\s*:\\s*(.*)$`, "i");
      const match = paragraphText.match(captionPattern);

      existingDescription = match ? match[2].trim() : paragraphText || "Description";
    }

    // Create caption with dot separator and set bold
    targetParagraph.insertText(`${captionType} `, "Replace");
    await context.sync();

    await insertSeqField(context, captionType, targetParagraph.getRange("End"));
    await context.sync();

    targetParagraph.insertText(`. ${existingDescription}`, "End");
    await context.sync();

    // Set entire paragraph to bold
    targetParagraph.font.bold = true;
    await context.sync();

    // Add bookmark
    const bookmarkId = await generateUniqueBookmarkId(captionType);
    targetParagraph.getRange().insertBookmark(bookmarkId);
    await context.sync();

    // Create cross-references if section provided
    if (sectionRange) {
      await updateAllFields(context, captionType);
      targetParagraph.load("text");
      await context.sync();

      const tableLabel = extractTableLabel(targetParagraph.text.trim(), captionType);
      const searchPattern = generateSearchPattern(tableLabel, captionType);
      // Exclude only the caption paragraph so table header cells and content remain eligible for cross-referencing
      const excludeRanges = [targetParagraph.getRange()];

      try {
        await addCrossReference(context, searchPattern, bookmarkId, sectionRange, excludeRanges);
      } catch (error) {
        Logger.error(`wordCaptions.ts: Error creating cross-references:`, error);
      }
    }

    await updateAllFields(context, captionType);
  });
};

const SEQ_TYPE_SWITCH: Record<SeqType, string> = {
  arabic: "ARABIC",
  alphabetic_lower_caps: "alphabetic",
  alphabetic_upper_caps: "ALPHABETIC",
  roman_lower_caps: "roman",
  roman_upper_caps: "ROMAN",
};

const SEQ_TYPE_IDENTIFIER: Record<SeqType, string> = {
  arabic: "Table",
  alphabetic_lower_caps: "Table_AlphaLower",
  alphabetic_upper_caps: "Table_AlphaUpper",
  roman_lower_caps: "Table_RomanLower",
  roman_upper_caps: "Table_RomanUpper",
};

export const createImmediateCaption = async (
  context: Word.RequestContext,
  imageParagraphRange: Word.Range,
  bookmarkId: string,
  captionDescription: string,
  captionType: CaptionType,
  staticCaptionLabel: string | null = null,
  captionPlacement?: CaptionPlacement,
  id?: string,
  seqType?: SeqType
): Promise<Word.Paragraph> => {
  // Insert caption paragraph AFTER the image paragraph
  let insertLocation;
  if (captionType === "Figure") {
    insertLocation = Word.InsertLocation.after;
  } else {
    insertLocation = Word.InsertLocation.before;
  }
  if (captionPlacement) {
    insertLocation = captionPlacement;
  }
  const captionParagraph = imageParagraphRange.insertParagraph("", insertLocation);
  await context.sync();

  // Insert the COMPLETE caption text first
  const captionStart = `${captionType} `;
  const captionEnd = `. ${captionDescription}`;
  const normalizedStaticCaptionLabel = staticCaptionLabel?.trim() || null;
  const targetSeqSwitch = seqType ? SEQ_TYPE_SWITCH[seqType] : undefined;
  const targetSeqIdentifier = seqType ? (SEQ_TYPE_IDENTIFIER[seqType] || captionType) : captionType;
  const hasCustomSeq = !!(id && targetSeqSwitch);

  if (normalizedStaticCaptionLabel) {
    captionParagraph.insertText(normalizedStaticCaptionLabel, Word.InsertLocation.start);
    await context.sync();
  } else if (hasCustomSeq) {
    captionParagraph.insertText(`${captionType} `, Word.InsertLocation.start);
    await context.sync();

    const seqRange = captionParagraph.getRange("End");
    try {
      seqRange.insertField(
        Word.InsertLocation.start,
        Word.FieldType.seq,
        `${targetSeqIdentifier} \\* ${targetSeqSwitch}`,
        true
      );
      await context.sync();
    } catch (fieldError) {
      Logger.warn(
        `wordCaptions.ts: createImmediateCaption: failed to insert custom SEQ field (${targetSeqIdentifier}), falling back to static text:`,
        fieldError
      );
      const fallbackNumber = id || `${getNextCaptionNumber(captionType)}`;
      seqRange.insertText(`${fallbackNumber}`, Word.InsertLocation.start);
      await context.sync();
    }
  } else {
    // Insert beginning part
    captionParagraph.insertText(captionStart, Word.InsertLocation.start);
    await context.sync();

    // Insert SEQ field
    const seqRange = captionParagraph.getRange("End");
    try {
      seqRange.insertField(Word.InsertLocation.start, Word.FieldType.seq, `${captionType} \\* ARABIC`, true);
      await context.sync();
    } catch (fieldError) {
      Logger.warn(
        `wordCaptions.ts: createImmediateCaption: failed to insert SEQ field (${captionType}), falling back to static numbering:`,
        fieldError
      );
      const fallbackNumber = getNextCaptionNumber(captionType);
      seqRange.insertText(`${fallbackNumber}`, Word.InsertLocation.start);
      await context.sync();
    }
  }

  // Insert ending part
  captionParagraph.insertText(captionEnd, Word.InsertLocation.end);
  await context.sync();

  // Ensure line break after caption to separate from content below
  captionParagraph.insertBreak(Word.BreakType.line, Word.InsertLocation.after);
  await context.sync();

  // Apply consistent formatting to the ENTIRE paragraph
  const entireCaptionRange = captionParagraph.getRange();
  entireCaptionRange.font.bold = true;
  await context.sync();

  await applyScriptMarkerFormattingToRange(context, entireCaptionRange);

  // Load the paragraph to check its content
  captionParagraph.load("text");
  await context.sync();

  Logger.debug(`wordCaptions.ts: Caption paragraph text before bookmark: "${captionParagraph.text}"`);

  // Extract ONLY the first line (the actual caption)
  const fullParagraphText = captionParagraph.text;
  // Get just "Figure X. Title"
  const captionLineOnly = fullParagraphText.split("\n")[0];

  Logger.debug(`wordCaptions.ts: Extracted caption line: "${captionLineOnly}"`);

  // Compute just the prefix + SEQ number
  const captionStartText = `${captionType} `;
  // extract number or label from paragraph text
  const seqNumber = captionParagraph.text.match(/\d+/)?.[0];
  const matchedLabel = captionParagraph.text.match(new RegExp(`${captionType}\\s+([A-Za-z0-9]+(?:[.#-][A-Za-z0-9]+)*)`, "i"))?.[0];
  const bookmarkText = normalizedStaticCaptionLabel || matchedLabel || (seqNumber ? captionStartText + seqNumber : captionType);

  // Search for only "Table 6" or "Table A" instead of entire caption line
  let captionSearch = captionParagraph.search(bookmarkText, {
    matchCase: false,
    matchWholeWord: false,
    ignorePunct: false,
    ignoreSpace: false,
  });
  captionSearch.load("items");
  await context.sync();

  if (captionSearch.items.length === 0) {
    captionSearch = captionParagraph.search(captionType, {
      matchCase: false,
      matchWholeWord: false,
      ignorePunct: false,
      ignoreSpace: false,
    });
    captionSearch.load("items");
    await context.sync();
  }

  if (captionSearch.items.length === 0) {
    throw new Error(`Could not find text "${bookmarkText}" to bookmark`);
  }

  // Bookmark only the first match (the number portion)
  captionSearch.items[0].insertBookmark(bookmarkId);
  await context.sync();

  Logger.debug(`wordCaptions.ts: Bookmark text: "${captionSearch.items[0].text}"`);

  // Verify the bookmark was created correctly
  const verifyBookmark = context.document.getBookmarkRangeOrNullObject(bookmarkId);
  verifyBookmark.load("isNullObject, text");
  await context.sync();

  if (!verifyBookmark.isNullObject) {
    Logger.debug(`wordCaptions.ts: Bookmark "${bookmarkId}" created successfully`);
    Logger.debug(`wordCaptions.ts: Bookmark spans: "${verifyBookmark.text}"`);

    // WARNING: Check if bookmark is too large
    const bookmarkLineCount = verifyBookmark.text.split("\n").filter((line) => line.trim()).length;
    if (bookmarkLineCount > 1) {
      Logger.warn(`wordCaptions.ts: WARNING: Bookmark spans ${bookmarkLineCount} lines (expected 1)`);
      Logger.warn(`wordCaptions.ts: This may cause cross-reference errors!`);
    }
  } else {
    Logger.error(`wordCaptions.ts: Failed to create bookmark "${bookmarkId}"`);
  }

  Logger.debug(`wordCaptions.ts: Created immediate caption: "${captionType}. ${captionDescription}"`);

  return captionParagraph;
};

export const createCrossReferences = async (
  context: Word.RequestContext,
  sectionRange: Word.Range,
  captionData: Array<{ bookmarkId: string; captionParagraph: Word.Paragraph; staticReference?: string | null; refId?: string | null }>,
  excludeRanges: Word.Range[],
  captionType: CaptionType
) => {
  await Word.run(async () => {
    // Update all fields first to get correct numbers
    const fields = context.document.body.fields;
    fields.load(["items", "code", "type"]);
    await context.sync();

    const figureFields = fields.items.filter(
      (field) => field.type === Word.FieldType.seq && field.code.includes(captionType)
    );
    figureFields.forEach((field) => field.updateResult());
    await context.sync();

    Logger.debug("wordCaptions.ts: Details of figure fields", figureFields);

    // Log the section text to see what we're searching in
    sectionRange.load("text");
    await context.sync();
    Logger.debug("wordCaptions.ts: Section text preview:", sectionRange.text.substring(0, 500));

    const sortedCaptionData = [...captionData].sort(
      (a, b) => (b.staticReference?.length || 0) - (a.staticReference?.length || 0)
    );

    for (const captionItem of sortedCaptionData) {
      const { bookmarkId, captionParagraph, refId } = captionItem;
      let staticReference = captionItem.staticReference;

      if (!refId && !staticReference && captionParagraph) {
        try {
          captionParagraph.load("text");
          await context.sync();
          const match = captionParagraph.text?.match(
            new RegExp(`${captionType}\\s+([A-Za-z0-9]+(?:[.#-][A-Za-z0-9]+)*)`, "i")
          );
          if (match) {
            staticReference = match[0];
          }
        } catch (_) {}
      }

      Logger.debug("wordCaptions.ts: Caption paragraph for current caption item:", captionParagraph);

      if (!refId && !staticReference) {
        Logger.warn(`wordCaptions.ts: Skipping cross-ref for ${bookmarkId}: No refId or static reference text was found.`);
        continue;
      }

      let searchPatterns: string[] = [];
      let matchCase = false;
      let matchWholeWord = false;

      if (refId) {
        const cleanRefId = refId.replace(/^\[+|\]+$/g, "").trim();
        const prefix = captionType === "Figure" ? "Figure" : "Table";
        const prefixLower = captionType === "Figure" ? "figure" : "table";
        searchPatterns = [
          `${prefix} [${cleanRefId}]`,
          `${prefixLower} [${cleanRefId}]`,
          `${prefix} [${refId}]`,
          `${prefixLower} [${refId}]`,
          `${prefix} ${cleanRefId}`,
          `${prefixLower} ${cleanRefId}`,
        ];
        matchCase = false;
        matchWholeWord = false;
      } else if (staticReference) {
        searchPatterns = [staticReference];
        matchWholeWord = !/[.#-]/.test(staticReference);

        if (/[a-zA-Z]$/.test(staticReference)) {
          matchCase = true;
          if (staticReference.startsWith("Table ")) {
            searchPatterns.push("table " + staticReference.substring(6));
            searchPatterns.push("TABLE " + staticReference.substring(6));
          } else if (staticReference.startsWith("Figure ")) {
            searchPatterns.push("figure " + staticReference.substring(7));
            searchPatterns.push("FIGURE " + staticReference.substring(7));
          }
        }
      }

      for (const searchPattern of searchPatterns) {
        Logger.debug(
          `wordCaptions.ts: Searching for STATIC reference: "${searchPattern}" to link to bookmark: ${bookmarkId}`
        );

        try {
          const searchResults = sectionRange.search(searchPattern, {
            ignorePunct: false,
            ignoreSpace: false,
            matchCase: matchCase,
            matchWholeWord: matchWholeWord,
            matchWildcards: false,
          });

        searchResults.load(["items"]);
        await context.sync();
        Logger.debug(`wordCaptions.ts: Search results count: ${searchResults.items.length}`);
        Logger.debug(`wordCaptions.ts: All items of search results:`, searchResults.items);

        if (searchResults.items.length === 0) {
          Logger.debug(`wordCaptions.ts: No cross-references found for "${searchPattern}"`);
          continue;
        }

        // Load text for all results
        searchResults.items.forEach((result) => result.load(["text"]));
        await context.sync();

        // Log all search results before filtering
        Logger.debug(
          "wordCaptions.ts: All search results:",
          searchResults.items.map((r) => r.text)
        );

        // Reload exclude ranges to account for document changes
        excludeRanges.forEach((range) => {
          range.load(["start", "end", "text"]);
        });
        await context.sync();

        // Log exclude range texts
        Logger.debug(
          "wordCaptions.ts: Exclude ranges:",
          excludeRanges.map((r) => r.text.substring(0, 50))
        );

        // Filter out excluded ranges (captions themselves)
        const validResults: Word.Range[] = [];

        for (const result of searchResults.items) {
          let isExcluded = false;

          for (const excludeRange of excludeRanges) {
            const relation = result.compareLocationWith(excludeRange);
            await context.sync();

            Logger.debug("wordCaptions.ts: compare location with works", relation.value);

            if (
              relation.value === Word.LocationRelation.equal ||
              relation.value === Word.LocationRelation.inside ||
              relation.value === Word.LocationRelation.insideStart ||
              relation.value === Word.LocationRelation.insideEnd
            ) {
              isExcluded = true;
              Logger.debug(`wordCaptions.ts: Excluding "${result.text}" because it's inside caption range`);
              break;
            }
          }

          if (!isExcluded) {
            validResults.push(result);
            Logger.debug(`wordCaptions.ts: Valid reference found: "${result.text}"`);
          }
        }

        await context.sync();

        // Insert cross-reference fields for valid matches
        for (const ref of validResults) {
          Logger.debug("wordCaptions.ts: Checking ref array's details", ref);
          // Store original formatting
          ref.load(["font/name", "font/size", "font/bold", "font/italic", "font/color", "font/highlightColor"]);
          await context.sync();

          const originalFont = {
            name: ref.font.name,
            size: ref.font.size,
            bold: ref.font.bold,
            italic: ref.font.italic,
            color: ref.font.color,
            highlightColor: ref.font.highlightColor,
          };

          // Right before creating cross-references
          // console.log(`Processing Figure ${figureNumber}`);
          Logger.debug(`wordCaptions.ts: Bookmark ID: ${captionItem.bookmarkId}`);

          // Verify the bookmark exists
          const testBookmark = context.document.getBookmarkRangeOrNullObject(captionItem.bookmarkId);
          testBookmark.load("isNullObject");
          await context.sync();

          if (testBookmark.isNullObject) {
            Logger.warn(`wordCaptions.ts: BOOKMARK "${captionItem.bookmarkId}" DOESN'T EXIST!`);
            continue;
          }

          Logger.debug(`wordCaptions.ts: Bookmark exists`);
          testBookmark.load("text");
          await context.sync();
          Logger.debug(`wordCaptions.ts: Bookmark points to: "${testBookmark.text}"`);

          Logger.debug(
            "wordCaptions.ts: Figure bookmarks:",
            captionData.map((f) => ({
              id: f.bookmarkId,
              captionText: f.captionParagraph.text,
            }))
          );

          // Then for each figure
          Logger.debug(`wordCaptions.ts: \nSearching for: "${searchPattern}"`);
          Logger.debug(`wordCaptions.ts: Bookmark to link to: "${captionItem.bookmarkId}"`);
          Logger.debug(`wordCaptions.ts: Valid results found: ${validResults.length}`);

          // Insert REF field. The bookmark spans the exact visible label,
          // so do not force Word's numeric format; that truncates labels like "Table 1.2-1".
          Logger.debug("wordCaptions.ts: Checking if valid bookmark is present", captionItem.bookmarkId);
          const fieldCode = `${captionItem.bookmarkId} \\h`;
          ref.insertField(Word.InsertLocation.replace, Word.FieldType.ref, fieldCode, true);
          await context.sync();
          excludeRanges.push(ref);

          Logger.debug("wordCaptions.ts: Checking if valid bookmark is present", fieldCode);

          Logger.debug("wordCaptions.ts: Insert REF field worked");

          // Restore and enhance formatting
          ref.load(["font/name", "font/size", "font/bold", "font/italic", "font/color", "font/highlightColor"]);
          await context.sync();

          Logger.debug("wordCaptions.ts: Restoring and enhancing formatting worked");

          ref.font.name = originalFont.name;
          ref.font.size = originalFont.size;
          ref.font.bold = true;
          ref.font.italic = originalFont.italic;
          ref.font.color = originalFont.color;
          ref.font.highlightColor = originalFont.highlightColor;
          ref.font.underline = Word.UnderlineType.single;

          await context.sync();
          Logger.debug(`wordCaptions.ts: Cross-reference inserted for ${captionItem.bookmarkId}`);
        }
      } catch (error) {
        Logger.error(`wordCaptions.ts: Error creating cross-references for "${searchPattern}":`, error);
      }
      }
    }

    Logger.debug("wordCaptions.ts: " + captionType + " cross-reference processing complete");
  });
};